import { v4 as uuidv4 } from 'uuid';
import type { StateCreator } from 'zustand';

import type { Gender } from '@/constants/enums';
import { getWardrobeCategoriesByGender } from '@/constants/generated';
import { clearLibraryDriveCache, deleteCharacterFolderFromDrive, deleteFileFromDrive, loadLibraryFromDrive, saveJsonToFolder, saveLibraryToDrive } from '@/services/driveService';
import { mergeUserItemsIntoCategories } from '@/services/userItemsStorage';
import { clearWardrobeItemsDriveCache, loadWardrobeItemsFromDrive } from '@/services/wardrobeItemsStorageService';
import type { PendingGeneration } from '@/types';
import type { LibraryCharacter, LookVariant } from '@/types/library';

import { initialCharacter } from './characterSlice';
import { initialGeneration } from './generationSlice';
import { buildSyntheticResult, getUserUploadedItems } from './helpers';
import type { IWardrobeStore } from './types';

export const initialLibrary: IWardrobeStore['library'] = {
  characters: [] as LibraryCharacter[],
  activeCharacterId: null as string | null,
  activeLookId: null as string | null,
  pendingGenerations: [] as PendingGeneration[],
  activePendingId: null as string | null,
  isSaving: false,
  saveError: null as string | null,
};

/**
 * Library slice — saved characters + their generated looks, plus pending
 * generation tracking and the auth-driven load/reset actions.
 *
 * Heavy cross-slice writes are expected here (every action that selects /
 * deletes / restores a library entry has to rebuild character + wardrobe +
 * generation to match). The slice `set` accepts whole-store writes; we rely
 * on convention to keep the entry point semantically library-shaped.
 */
export type LibrarySliceContribution = Pick<
  IWardrobeStore,
  | 'library'
  | 'loadUserWardrobeItems'
  | 'resetUserState'
  | 'loadUserLibrary'
  | 'addCharacterToLibrary'
  | 'addLookToCharacter'
  | 'selectLook'
  | 'selectCharacter'
  | 'deleteCharacter'
  | 'deleteLook'
  | 'clearActiveEntry'
  | 'updateCharacterImageUrl'
  | 'setCharacterDriveSubfolder'
  | 'addPendingGeneration'
  | 'setPendingExecutionId'
  | 'pruneAndCollectResumablePendings'
  | 'removePendingGeneration'
  | 'detachFromGeneration'
  | 'viewPendingGeneration'
  | 'setLibrarySaving'
  | 'setLibrarySaveError'
>;

export const createLibrarySlice: StateCreator<IWardrobeStore, [], [], LibrarySliceContribution> = (set, get) => ({
  library: initialLibrary,

  // --- Auth-driven Actions ---

  loadUserWardrobeItems: () => {
    clearWardrobeItemsDriveCache();
    loadWardrobeItemsFromDrive()
      .then(items => {
        if (!items || items.length === 0) return;
        set(s => ({
          wardrobe: {
            ...s.wardrobe,
            categories: mergeUserItemsIntoCategories(s.wardrobe.categories, items),
          },
        }));
      })
      .catch(err => {
        console.error('[Store] Failed to load wardrobe items from Drive:', err);
      });
  },

  resetUserState: () => {
    set(s => ({
      character: { ...initialCharacter },
      wardrobe: {
        ...s.wardrobe,
        selectedItems: [],
      },
      generation: { ...initialGeneration },
      library: { ...initialLibrary },
    }));
  },

  loadUserLibrary: () => {
    clearLibraryDriveCache();
    loadLibraryFromDrive()
      .then(entries => {
        if (!entries) return;
        set(s => ({ library: { ...s.library, characters: entries } }));
      })
      .catch(err => {
        console.error('[Store] Failed to load library from Drive:', err);
      });
    get().loadUserWardrobeItems();
  },

  // --- Library Actions ---

  addCharacterToLibrary: (characterImageUrl: string, gender: Gender) => {
    const id = uuidv4();
    const entry: LibraryCharacter = {
      id,
      characterImageUrl,
      driveUidCharacter: null,
      timestamp: Date.now(),
      gender,
      looks: [],
    };
    set(s => {
      // Check if this character image already exists
      const existing = s.library.characters.find(c => c.characterImageUrl === characterImageUrl);
      if (existing) return { library: { ...s.library, activeCharacterId: existing.id, activeLookId: null } };

      const newChars = [entry, ...s.library.characters];
      saveLibraryToDrive(newChars);
      return { library: { ...s.library, characters: newChars, activeCharacterId: id, activeLookId: null } };
    });
    // Return the ID (either new or existing)
    const state = get();
    const found = state.library.characters.find(c => c.characterImageUrl === characterImageUrl);
    return found?.id ?? id;
  },

  addLookToCharacter: (characterId: string, look: LookVariant) => {
    set(s => {
      const newChars = s.library.characters.map(c => {
        if (c.id !== characterId) return c;
        return { ...c, looks: [look, ...c.looks] };
      });
      saveLibraryToDrive(newChars);
      // Persist the look's metadata (selectedItems, presets, hair color) into
      // the per-character JSON so it survives reload. Without this, on refresh
      // the orphan reconcile in loadLibraryFromDrive recreates the look as a
      // bare entry — the generated image stays, but the selections are lost.
      const updatedChar = newChars.find(c => c.id === characterId);
      if (updatedChar?.driveSubfolderUid) {
        void saveJsonToFolder(JSON.stringify(updatedChar), 'ai-wardrobe-character.json', updatedChar.driveSubfolderUid);
      }

      const isOnCanvas = s.generation.currentResult?.id === look.id;
      return {
        library: {
          ...s.library,
          characters: newChars,
          activeCharacterId: isOnCanvas ? characterId : s.library.activeCharacterId,
          activePendingId: isOnCanvas ? null : s.library.activePendingId,
          activeLookId: isOnCanvas ? look.id : s.library.activeLookId,
        },
      };
    });
  },

  selectLook: (characterId: string, lookId: string) => {
    const s = get();
    const char = s.library.characters.find(c => c.id === characterId);
    if (!char) return;
    const look = char.looks.find(l => l.id === lookId);
    if (!look) return;

    const presetCategories = getWardrobeCategoriesByGender(char.gender);
    const currentUserItems = getUserUploadedItems(s.wardrobe.categories);

    // Build history from all looks of this character (newest-first) so ResultNavigator can navigate between them
    const characterHistory = [...char.looks].sort((a, b) => b.timestamp - a.timestamp).map(l => buildSyntheticResult(l, char));

    set({
      library: { ...s.library, activeCharacterId: characterId, activeLookId: lookId, activePendingId: null },
      character: {
        ...s.character,
        imageUrl: char.characterImageUrl,
        imageFile: null,
        imageAspectRatio: null,
        gender: char.gender,
        selectedStyle: look.selectedStyle,
        selectedPose: look.selectedPose,
        selectedBackground: look.selectedBackground,
        selectedHairstyle: look.selectedHairstyle,
        selectedHairColor: look.selectedHairColor,
        selectedRetouchPresets: look.selectedRetouchPresets ?? {},
      },
      wardrobe: {
        ...s.wardrobe,
        categories: mergeUserItemsIntoCategories(presetCategories, currentUserItems),
        selectedItems: look.selectedItems,
      },
      generation: {
        ...s.generation,
        isViewingGeneration: false,
        currentResult: buildSyntheticResult(look, char),
        history: characterHistory,
        viewedHistoryItem: null,
        variants: [],
        error: null,
      },
    });
  },

  selectCharacter: (characterId: string) => {
    const s = get();
    const char = s.library.characters.find(c => c.id === characterId);
    if (!char) return;

    const presetCategories = getWardrobeCategoriesByGender(char.gender);
    const currentUserItems = getUserUploadedItems(s.wardrobe.categories);
    const preserveGen = s.generation.isGenerating;

    set({
      library: { ...s.library, activeCharacterId: characterId, activeLookId: null, activePendingId: null },
      character: {
        ...s.character,
        imageUrl: char.characterImageUrl,
        imageFile: null,
        imageAspectRatio: null,
        gender: char.gender,
        selectedStyle: null,
        selectedPose: null,
        selectedBackground: null,
        selectedHairstyle: null,
        selectedRetouchPresets: {},
      },
      wardrobe: {
        ...s.wardrobe,
        categories: mergeUserItemsIntoCategories(presetCategories, currentUserItems),
        selectedItems: [],
      },
      generation: preserveGen ? { ...s.generation, isViewingGeneration: false, viewedHistoryItem: null, currentResult: null, variants: [], error: null } : { ...initialGeneration },
    });
  },

  deleteCharacter: (characterId: string) => {
    // Capture the Drive subfolder uid BEFORE we drop the character from the
    // store so we can clean it up async — fire-and-forget.
    const charBefore = get().library.characters.find(c => c.id === characterId);
    if (charBefore?.driveSubfolderUid) {
      void deleteCharacterFolderFromDrive(charBefore.driveSubfolderUid);
    }

    set(s => {
      const newChars = s.library.characters.filter(c => c.id !== characterId);
      saveLibraryToDrive(newChars);

      const isActive = s.library.activeCharacterId === characterId;
      const remainingPending = s.library.pendingGenerations.filter(p => p.characterId !== characterId);
      const removedPendingCount = s.library.pendingGenerations.length - remainingPending.length;
      const hadPendingForChar = removedPendingCount > 0;

      return {
        library: {
          ...s.library,
          characters: newChars,
          activeCharacterId: isActive ? null : s.library.activeCharacterId,
          activeLookId: isActive ? null : s.library.activeLookId,
          pendingGenerations: remainingPending,
        },
        ...(isActive
          ? {
              character: { ...initialCharacter },
              wardrobe: {
                ...s.wardrobe,
                categories: mergeUserItemsIntoCategories(getWardrobeCategoriesByGender(initialCharacter.gender), getUserUploadedItems(s.wardrobe.categories)),
                selectedItems: [],
              },
              generation: { ...initialGeneration },
            }
          : hadPendingForChar
          ? {
              generation: {
                ...s.generation,
                isGenerating: remainingPending.length > 0,
                // Keep activeGenerationCount in sync with the pendings we just dropped — otherwise
                // isGenerating flips to false while the counter still reports in-flight work,
                // and the next generationSuccess underflows / displays the wrong state.
                activeGenerationCount: Math.max(0, s.generation.activeGenerationCount - removedPendingCount),
              },
            }
          : {}),
      };
    });
  },

  deleteLook: (characterId: string, lookId: string) => {
    const lookBefore = get()
      .library.characters.find(c => c.id === characterId)
      ?.looks.find(l => l.id === lookId);
    if (lookBefore?.driveUidGenerated) {
      void deleteFileFromDrive(lookBefore.driveUidGenerated);
    }

    set(s => {
      const newChars = s.library.characters.map(c => {
        if (c.id !== characterId) return c;
        return { ...c, looks: c.looks.filter(l => l.id !== lookId) };
      });
      saveLibraryToDrive(newChars);

      const updatedChar = newChars.find(c => c.id === characterId);
      if (updatedChar?.driveSubfolderUid) {
        void saveJsonToFolder(JSON.stringify(updatedChar), 'ai-wardrobe-character.json', updatedChar.driveSubfolderUid);
      }

      const isActiveLook = s.library.activeLookId === lookId;
      return {
        library: {
          ...s.library,
          characters: newChars,
          activeLookId: isActiveLook ? null : s.library.activeLookId,
        },
        ...(isActiveLook ? { generation: { ...initialGeneration } } : {}),
      };
    });
  },

  clearActiveEntry: () => {
    set(s => {
      const preserveGen = s.generation.isGenerating;
      return {
        library: { ...s.library, activeCharacterId: null, activeLookId: null, activePendingId: null },
        character: { ...initialCharacter },
        wardrobe: {
          ...s.wardrobe,
          categories: mergeUserItemsIntoCategories(getWardrobeCategoriesByGender(initialCharacter.gender), getUserUploadedItems(s.wardrobe.categories)),
          selectedItems: [],
        },
        generation: preserveGen ? { ...s.generation, isViewingGeneration: false, currentResult: null, variants: [], error: null } : { ...initialGeneration },
      };
    });
  },

  updateCharacterImageUrl: (characterId: string, newUrl: string) => {
    set(s => {
      const newChars = s.library.characters.map(c => (c.id === characterId ? { ...c, characterImageUrl: newUrl } : c));
      saveLibraryToDrive(newChars);
      // Also update the active character image if this is the current one
      const isActive = s.library.activeCharacterId === characterId;
      return {
        library: { ...s.library, characters: newChars },
        ...(isActive ? { character: { ...s.character, imageUrl: newUrl } } : {}),
      };
    });
  },

  setCharacterDriveSubfolder: (characterId: string, uid: string, name: string) => {
    set(s => ({
      library: {
        ...s.library,
        characters: s.library.characters.map(c => (c.id === characterId ? { ...c, driveSubfolderUid: uid, driveSubfolderName: name } : c)),
      },
    }));
  },

  // --- Pending Generation Actions ---

  addPendingGeneration: pending => {
    set(s => ({
      library: { ...s.library, pendingGenerations: [...s.library.pendingGenerations, pending], activePendingId: pending.id, activeLookId: null },
    }));
  },

  setPendingExecutionId: (id, executionId) => {
    set(s => ({
      library: {
        ...s.library,
        pendingGenerations: s.library.pendingGenerations.map(p => (p.id === id ? { ...p, executionId } : p)),
      },
    }));
  },

  pruneAndCollectResumablePendings: () => {
    const PENDING_TTL_MS = 15 * 60 * 1000;
    // If a pending has no executionId, the submitWebhook call never returned
    // before reload. We auto-restart only if the user just reloaded (within
    // this window) — older orphans likely already submitted server-side, so
    // restarting them could double-bill.
    const ORPHAN_RESTART_WINDOW_MS = 60 * 1000;
    const now = Date.now();
    const all = get().library.pendingGenerations;
    if (all.length === 0) return [];

    const fresh = all.filter(p => {
      const age = now - p.timestamp;
      if (age > PENDING_TTL_MS) return false;
      if (!p.executionId && age > ORPHAN_RESTART_WINDOW_MS) return false;
      return true;
    });
    if (fresh.length !== all.length) {
      set(s => ({
        library: {
          ...s.library,
          pendingGenerations: fresh,
          activePendingId: fresh.some(p => p.id === s.library.activePendingId) ? s.library.activePendingId : null,
        },
        generation: { ...s.generation, isGenerating: fresh.length > 0 },
      }));
    } else if (fresh.length > 0) {
      set(s => ({ generation: { ...s.generation, isGenerating: true } }));
    }

    return fresh;
  },

  removePendingGeneration: id => {
    set(s => {
      const remaining = s.library.pendingGenerations.filter(p => p.id !== id);
      return {
        library: { ...s.library, pendingGenerations: remaining, activePendingId: s.library.activePendingId === id ? null : s.library.activePendingId },
        generation: { ...s.generation, isGenerating: remaining.length > 0 },
      };
    });
  },

  detachFromGeneration: () => {
    set(s => ({
      generation: {
        ...s.generation,
        isViewingGeneration: false,
        currentResult: null,
        viewedHistoryItem: null,
        variants: [],
        error: null,
        // Invalidate any in-flight generation so its completion fails the
        // isLatest check in generationSuccess and can't yank the canvas back.
        latestGenerationId: s.generation.latestGenerationId + 1,
      },
      character: {
        ...s.character,
        selectedStyle: null,
        selectedPose: null,
        selectedBackground: null,
        selectedHairstyle: null,
        selectedRetouchPresets: {},
      },
      wardrobe: { ...s.wardrobe, selectedItems: [] },
      library: { ...s.library, activeLookId: null, activePendingId: null },
    }));
  },

  viewPendingGeneration: id => {
    const s = get();
    const pending = s.library.pendingGenerations.find(p => p.id === id);
    if (!pending) return;

    const presetCategories = getWardrobeCategoriesByGender(pending.snapshot.character.gender);
    const currentUserItems = getUserUploadedItems(s.wardrobe.categories);

    set({
      library: { ...s.library, activeCharacterId: pending.characterId, activeLookId: null, activePendingId: id },
      character: { ...pending.snapshot.character },
      wardrobe: {
        ...s.wardrobe,
        categories: mergeUserItemsIntoCategories(presetCategories, currentUserItems),
        selectedItems: pending.snapshot.selectedItems,
      },
      generation: {
        ...s.generation,
        isViewingGeneration: true,
        currentResult: null,
        variants: [],
        error: null,
      },
    });
  },

  setLibrarySaving: (saving: boolean) => set(s => ({ library: { ...s.library, isSaving: saving } })),
  setLibrarySaveError: (error: string | null) => set(s => ({ library: { ...s.library, saveError: error } })),
});
